import { fg } from '@atlaskit/platform-feature-flags';
import { getSessionItem } from '@atlassian/search-dialog/session-storage-utils';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import {
	type AIAnswerQueryStream,
	type AIAnswerQueryStreamEntry,
	type AIAnswerQueryStreamMarkdownFinalPart,
	AIAnswerQueryStreamType,
	NLPSearchErrorCodes,
	NLPSearchErrorState,
	NLPSearchResultFormat,
	type NLPSearchResultSource,
	type NLPSearchType,
	type SearchAIAnswerQueryType,
	type SearchAIAnswerQueryVariables,
} from '../ai-answer-dialog/types';
import { logger } from '../common/utils/logger';
import {
	ASSISTANCE_SERVICE_API_BASE_URL,
	PROMPT_OVERRIDE_SESSION_KEY,
	SAIN_STREAMING_API,
} from '../constants';

import { type AgentError, type AgentResponse, type StreamError } from './types';
import { mapStreamDefinitionResponse, mapStreamSAINResponse } from './utils';

const identityUserCloudARIPrefix = 'ari:cloud:identity::user/';

function getAgentAndExperience(isReadingAids: boolean) {
	return isReadingAids
		? {
				named_id: 'reading_aids_agent',
				experience: 'reading-aids',
			}
		: {
				named_id: 'sain_agent',
				experience: 'sain',
			};
}

function mapMimeTypeToFormat(mimeType: string): NLPSearchResultFormat {
	switch (mimeType) {
		case 'text/markdown':
			return NLPSearchResultFormat.MARKDOWN;
		case 'text/adf':
			return NLPSearchResultFormat.ADF;
		case 'text/json':
			return NLPSearchResultFormat.JSON;
		default:
			return NLPSearchResultFormat.MARKDOWN;
	}
}

type MappedError = {
	errorState: NLPSearchErrorState;
	networkErrorReason?: string;
	networkErrorDetails?: string;
	backendFailureType?: string;
};

function mapErrorToSain(error: StreamError | AgentError, traceId?: string | null): MappedError {
	const errorMessage =
		'message_template' in error ? error.message_template : error.message.message_template;

	switch (errorMessage) {
		case 'NO_ANSWER_LLM':
			return { errorState: NLPSearchErrorState.NoAnswer };
		case 'NO_ANSWER_PEOPLE':
			return { errorState: NLPSearchErrorState.NoAnswerWhoQuestion };
		case 'NO_ANSWER_KEYWORDS':
			return { errorState: NLPSearchErrorState.NoAnswerKeywords };
		case 'NO_ANSWER_SEARCH_QA_PLUGIN':
			return { errorState: NLPSearchErrorState.NoAnswerSearchResults };
		case 'ACCEPTABLE_USE_VIOLATIONS':
			return { errorState: NLPSearchErrorState.AcceptableUseViolation };
		case 'OPENAI_RATE_LIMIT_USER_ABUSE':
			return { errorState: NLPSearchErrorState.NoAnswerOpenAIRateLimitUserAbuse };
		case 'FEATURE_DISABLED_ON_SITE':
			return { errorState: NLPSearchErrorState.AIDisabled };
		default:
			logger.error({
				error,
				functionName: 'mapErrorToSain: NetworkError',
				traceId,
			});
			return {
				errorState: NLPSearchErrorState.NetworkError,
				networkErrorReason: 'unmapped_backend_error',
				networkErrorDetails: errorMessage,
				backendFailureType: 'unmapped_template',
			};
	}
}

function mapMessageToNLPSearchType(
	message: AgentResponse,
	requestId?: string | null,
): NLPSearchType {
	// Handle cases where message is directly in root or nested under message.message
	const messageContent = message.message || message;

	return {
		nlpResult: messageContent.content,
		uniqueSources: message.sources
			? message.sources.map(
					(source): NLPSearchResultSource => ({
						ari: source.ari,
						id: source.id?.toString(),
						title: source.title,
						product: source.product,
						type: source.type,
						url: source.url,
						lastModified: source.lastModified,
						iconUrl: null,
						spaceName: null,
						spaceUrl: null,
					}),
				)
			: [],
		disclaimer: messageContent.message_metadata?.disclaimer ?? null,
		errorState: null,
		format: mapMimeTypeToFormat(messageContent.content_mime_type),
		extraAPIAnalyticsAttributes: {
			message_id: messageContent.id,
			conversational_channel_id: messageContent.conversation_channel_id,
			experience_id: messageContent.experience_id,
			user_id: messageContent.user_ari?.replace(identityUserCloudARIPrefix, '') ?? null,
			userQuery: message.human_message?.content ?? null,
			requestId: fg('kd_fix_sain_mau_instrumentation')
				? (requestId ?? null)
				: (message.message?.message_metadata?.request_id ?? null),
			answerGenerationModel: message.usage_data ?? null,
			...(!fg('kd_consolidate_sources_analytics') && { sources: message.sources }),
			cacheInfo: message.message?.message_metadata?.global_cache_info ?? null,
			pluginInvocations: message.message?.plugin_invocations,
			rawJson: message,
			...(fg('kd_sain_online_debugger') && {
				minion_outputs: message.message?.minion_outputs,
				traceId: requestId ?? null,
			}),
		},
	};
}

export type FetchConfig = {
	hostName?: string;
	credentials?: 'omit' | 'same-origin' | 'include';
	entity_ari?: string;
	headers?: { [key: string]: string };
	timeout?: number;
};

type AskSainFetchHandlerArgs = {
	fetchReturn?: Promise<Response>;
	variables?: SearchAIAnswerQueryVariables;
	fetchConfig?: FetchConfig;
};

export async function* sainStreamFetchHandler({
	fetchReturn,
	variables,
	fetchConfig,
}: AskSainFetchHandlerArgs): AIAnswerQueryStream {
	try {
		const { query, locale, experience, filters, cloudIdARI, followUpsEnabled } = variables || {};
		const url = `${fetchConfig?.hostName || ''}${SAIN_STREAMING_API}`;
		const response = await (fetchReturn
			? fetchReturn
			: fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						query,
						filters,
						locations: [cloudIdARI],
						locale,
						experience,
						followUpsEnabled,
					}),
					credentials: fetchConfig?.credentials || 'same-origin', // fetch() uses 'same-origin' by default
				}));

		if (!response.ok) {
			// For non 200 status codes
			return {
				state: 'failed',
				reason: 'backend',
				details: 'Unhandled error response received',
				errorCode: response.status,
			};
		}

		if (!response.body) {
			return {
				state: 'failed',
				reason: 'backend',
				details: 'response.body missing',
				errorCode: response.status,
			};
		}

		try {
			const reader = response.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let buffer = '';
			let done = false;

			let finalResponse: AIAnswerQueryStreamMarkdownFinalPart['value'] | undefined;

			while (!done) {
				const { value, done: doneReading } = await reader.read();
				done = doneReading;
				const chunkValue = decoder.decode(value);
				buffer = buffer + chunkValue;

				// Split the buffer by line breaks
				const lines = buffer.split('\n');
				// Process all complete lines, except for the last one (which might be incomplete)
				while (lines.length > 1) {
					const line = lines.shift()!;
					const parsedData = JSON.parse(line) as
						| AIAnswerQueryStreamEntry
						| AIAnswerQueryStreamMarkdownFinalPart['value'];

					if (
						parsedData.type === AIAnswerQueryStreamType.AnswerType ||
						parsedData.type === AIAnswerQueryStreamType.AnswerPart ||
						parsedData.type === AIAnswerQueryStreamType.GenerationType ||
						parsedData.type === AIAnswerQueryStreamType.Trace ||
						parsedData.type === AIAnswerQueryStreamType.ErrorStateQuerySuggestions ||
						parsedData.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage
					) {
						yield parsedData;
					} else {
						finalResponse = parsedData;
						done = true;
					}
				}
				// Keep the last (potentially incomplete) line in the buffer
				buffer = lines[0];
			}

			if (finalResponse?.type !== AIAnswerQueryStreamType.FinalResponse) {
				return {
					state: 'failed',
					reason: 'parsing',
					error: 'Unexpected final response value',
				};
			}

			return { state: 'complete', value: finalResponse };
		} catch (parsingError: unknown) {
			return { state: 'failed', reason: 'parsing', error: parsingError };
		}
	} catch (error: unknown) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			// this is likely an abort error
			return { state: 'failed', reason: 'aborted' };
		}

		if (error instanceof Error) {
			return { state: 'failed', reason: 'error', details: error.message, error };
		}

		return { state: 'failed', reason: 'unknown' };
	}
}

const getAgentInput = ({
	variables,
	fetchConfig,
}: {
	variables: SearchAIAnswerQueryVariables | undefined;
	fetchConfig: FetchConfig | undefined;
}) => {
	const { query, locale, additional_context, experimentContext, isQuerySuggestion } =
		variables || {};
	return {
		content: query,
		locale,
		additional_context,
		entity_ari: fetchConfig?.entity_ari,
		...(experimentContext && fg('kd_sain_backend_exp_context') && { experimentContext }),
		...(typeof isQuerySuggestion === 'boolean' ? { isQuerySuggestion } : {}),
	};
};

const getPromptOverrideContext = () => {
	if (!fg('convo_ai_sain_enable_prompt_override_hello_only')) {
		return {};
	}

	const promptOverride = getSessionItem(PROMPT_OVERRIDE_SESSION_KEY);
	if (!promptOverride) {
		return {};
	}

	return {
		aiFeatureContext: {
			systemPromptOverride: promptOverride,
		},
	};
};

export async function* sainStreamFetchConvoAIService({
	fetchReturn,
	variables,
	isReadingAids,
	fetchConfig,
	generatorConfig = {
		yieldAnswerPart: true,
		yieldFinalResponse: false,
	},
	storeMessage,
	onStreamFinalResponse,
}: AskSainFetchHandlerArgs & {
	isReadingAids: boolean;
	storeMessage?: { store_message?: boolean };
	generatorConfig?: {
		yieldAnswerPart?: boolean;
		yieldFinalResponse?: boolean;
	};
	onStreamFinalResponse?: () => void;
}): AIAnswerQueryStream {
	try {
		const { filters, selectedProduct } = variables || {};
		const { experience, named_id } = getAgentAndExperience(isReadingAids);

		const url = `${
			fetchConfig?.hostName || ''
		}${ASSISTANCE_SERVICE_API_BASE_URL}/rovo/v1/sain/stream`;

		const streamFilters: Record<string, unknown>[] =
			filters && !Array.isArray(filters) && fg('kd_add_common_filters_support_to_sain')
				? [
						{ commonFilters: filters.commonFilters },
						{ confluenceFilters: filters.confluenceFilters },
					]
				: [{ confluenceFilters: filters }];

		if (selectedProduct) {
			streamFilters.push({
				knowledgeSources: [selectedProduct],
			});
		}

		const response = await (fetchReturn
			? fetchReturn
			: fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Product': 'confluence',
						'X-Experience-Id': experience,
						...(fetchConfig?.headers ? fetchConfig.headers : {}),
					},
					body: JSON.stringify({
						recipient_agent_named_id: named_id,
						agent_input: {
							...getAgentInput({ variables, fetchConfig }),
							filters: filters ? streamFilters : undefined,
							...getPromptOverrideContext(),
						},
						...storeMessage,
					}),
					credentials: fetchConfig?.credentials || 'same-origin', // fetch() uses 'same-origin' by default
					signal: fetchConfig?.timeout ? AbortSignal.timeout(fetchConfig?.timeout) : undefined,
				}));

		const traceId = response.headers.get('x-trace-id') || null;
		if (!response.body) {
			return {
				state: 'failed',
				reason: 'backend',
				details: 'response.body missing',
				errorCode: response.status,
				backendFailureType: 'empty_body',
			};
		}

		try {
			const reader = response.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let buffer = '';
			let done = false;

			let finalResponse: AIAnswerQueryStreamMarkdownFinalPart['value'] | undefined;
			const pluginsInvoked: string[] = [];

			yield {
				type: AIAnswerQueryStreamType.AnswerType,
				message: {
					format: NLPSearchResultFormat.MARKDOWN,
				},
			};

			while (!done) {
				const { value, done: doneReading } = await reader.read();

				done = doneReading;
				const chunkValue = decoder.decode(value);
				buffer = buffer + chunkValue;

				// Split the buffer by line breaks
				const lines = buffer.split('\n');

				// add case here to return error from stream
				if (!response.ok) {
					const finalResponse = JSON.parse(lines[0]);
					if (finalResponse?.errors.includes(NLPSearchErrorCodes.HIPAA_ENABLED_ERROR)) {
						return {
							state: 'failed',
							reason: 'backend',
							details: NLPSearchErrorCodes.HIPAA_ENABLED_ERROR,
							errorCode: response.status,
							backendFailureType: 'hipaa',
						};
					}
					// For non 200 status codes
					return {
						state: 'failed',
						reason: 'backend',
						details: 'Unhandled error response received',
						errorCode: response.status,
						backendFailureType: 'http_error',
					};
				}

				// Process all complete lines, except for the last one (which might be incomplete)
				while (lines.length > 1) {
					const line = lines.shift()!;

					const parsedData = mapStreamSAINResponse(JSON.parse(line), traceId);

					if (parsedData.type === 'other') {
						continue;
					} else if (parsedData.type === 'plugin-invocation') {
						pluginsInvoked.push(parsedData.pluginName);
						continue;
					} else if (
						parsedData.type === AIAnswerQueryStreamType.AnswerType ||
						parsedData.type === AIAnswerQueryStreamType.AnswerPart ||
						parsedData.type === AIAnswerQueryStreamType.GenerationType ||
						parsedData.type === AIAnswerQueryStreamType.Trace ||
						parsedData.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage
					) {
						if (generatorConfig.yieldAnswerPart) {
							yield parsedData;
						}
					} else if (parsedData.type === 'follow-up') {
						if (!finalResponse?.message || !variables?.followUpsEnabled) {
							continue;
						}

						finalResponse.message.nlpFollowUpResults = {
							followUps: parsedData.queries,
						};

						const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
							abTestId: 'kd_sain_show_no_answer_suggestions_exp',
						});

						if (!serveErrorStateSuggestedQueriesExp) {
							done = true;
						}
					} else if (parsedData.type === AIAnswerQueryStreamType.ErrorStateQuerySuggestions) {
						if (!finalResponse?.message) {
							continue;
						}

						finalResponse.message.nlpSuggestedQueries = parsedData.suggested_queries;

						done = true;
					} else if (parsedData.type === AIAnswerQueryStreamType.EarlyStop) {
						return { state: 'complete', value: parsedData };
					} else {
						finalResponse = parsedData;
						onStreamFinalResponse?.();

						if (!variables?.followUpsEnabled) {
							done = true;
						}

						if (generatorConfig.yieldFinalResponse) {
							yield finalResponse;
						}
					}
				}
				// Keep the last (potentially incomplete) line in the buffer
				buffer = lines[0];
			}

			if (finalResponse?.type !== AIAnswerQueryStreamType.FinalResponse) {
				return {
					state: 'failed',
					reason: 'parsing',
					error: 'Unexpected final response value',
				};
			}

			if (fg('kd_add_plugins_invoked')) {
				const filteredPluginsInvoked = pluginsInvoked.filter(Boolean);
				if (filteredPluginsInvoked.length > 0 && finalResponse.message) {
					const existing =
						(finalResponse.message.extraAPIAnalyticsAttributes?.pluginsInvoked as
							| string[]
							| undefined) ?? [];
					finalResponse.message.extraAPIAnalyticsAttributes = {
						...finalResponse.message.extraAPIAnalyticsAttributes,
						pluginsInvoked: [...new Set([...existing, ...filteredPluginsInvoked])],
					};
				}
			}

			return { state: 'complete', value: finalResponse };
		} catch (parsingError: unknown) {
			return { state: 'failed', reason: 'parsing', error: parsingError };
		}
	} catch (error: unknown) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			return { state: 'failed', reason: 'aborted' };
		}

		if (error instanceof DOMException && error.name === 'TimeoutError') {
			return { state: 'failed', reason: 'timeout' };
		}

		if (error instanceof Error) {
			return { state: 'failed', reason: 'error', details: error.message, error };
		}

		return { state: 'failed', reason: 'unknown' };
	}
}

export async function sainRestFetchHandlerAssistanceService({
	variables,
	isReadingAids,
	fetchConfig,
}: AskSainFetchHandlerArgs & {
	isReadingAids: boolean;
}): Promise<SearchAIAnswerQueryType> {
	const { experience, named_id } = getAgentAndExperience(isReadingAids);

	const url = `${
		fetchConfig?.hostName || ''
	}${ASSISTANCE_SERVICE_API_BASE_URL}/chat/v1/invoke_agent`;

	const response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Product': 'confluence',
			'X-Experience-Id': experience,
			...fetchConfig?.headers,
		},
		body: JSON.stringify({
			recipient_agent_named_id: named_id,
			agent_input: getAgentInput({ variables, fetchConfig }),
		}),
		credentials: fetchConfig?.credentials || 'same-origin', // fetch() uses 'same-origin' by default
		signal: fetchConfig?.timeout ? AbortSignal.timeout(fetchConfig?.timeout) : undefined,
	});

	const traceId = response.headers.get('x-trace-id') || null;
	const data: AgentResponse | AgentError = await response.json();

	if ('message_template' in data) {
		const { errorState, networkErrorReason, networkErrorDetails, backendFailureType } =
			mapErrorToSain(data, traceId);
		return {
			nlpSearch: {
				errorState,
				networkErrorReason,
				networkErrorDetails,
				backendFailureType,
				disclaimer: null,
				format: NLPSearchResultFormat.MARKDOWN,
				uniqueSources: [],
			},
		};
	}

	return {
		nlpSearch: mapMessageToNLPSearchType(data, traceId),
	};
}

export async function* aiDefinitionsStreamFetchConvoAIService({
	fetchReturn,
	variables,
	fetchConfig,
	generatorConfig = {
		yieldAnswerPart: true,
		yieldFinalResponse: false,
	},
	onStreamFinalResponse,
}: AskSainFetchHandlerArgs & {
	generatorConfig?: {
		yieldAnswerPart?: boolean;
		yieldFinalResponse?: boolean;
	};
	onStreamFinalResponse?: () => void;
}): AIAnswerQueryStream {
	try {
		const url = `${
			fetchConfig?.hostName || ''
		}${ASSISTANCE_SERVICE_API_BASE_URL}/rovo/v1/ai-definition`;

		const response = await (fetchReturn
			? fetchReturn
			: fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Product': 'confluence',
						'X-Experience-Id': 'reading-aids',
						...(fetchConfig?.headers ? fetchConfig.headers : {}),
					},
					body: JSON.stringify({
						...getAgentInput({ variables, fetchConfig }),
					}),
					credentials: fetchConfig?.credentials || 'same-origin',
					signal: fetchConfig?.timeout ? AbortSignal.timeout(fetchConfig?.timeout) : undefined,
				}));

		const traceId = response.headers.get('x-trace-id') || null;
		if (!response.body) {
			return {
				state: 'failed',
				reason: 'backend',
				details: 'response.body missing',
				errorCode: response.status,
				backendFailureType: 'empty_body',
			};
		}

		try {
			const reader = response.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let buffer = '';
			let done = false;

			let finalResponse: AIAnswerQueryStreamMarkdownFinalPart['value'] | undefined;

			yield {
				type: AIAnswerQueryStreamType.AnswerType,
				message: {
					format: NLPSearchResultFormat.MARKDOWN,
				},
			};

			while (!done) {
				const { value, done: doneReading } = await reader.read();

				done = doneReading;
				const chunkValue = decoder.decode(value);
				buffer = buffer + chunkValue;

				// Split the buffer by line breaks
				const lines = buffer.split('\n');

				// add case here to return error from stream
				if (!response.ok) {
					const finalResponse = JSON.parse(lines[0]);
					if (finalResponse?.errors.includes(NLPSearchErrorCodes.HIPAA_ENABLED_ERROR)) {
						return {
							state: 'failed',
							reason: 'backend',
							details: NLPSearchErrorCodes.HIPAA_ENABLED_ERROR,
							errorCode: response.status,
							backendFailureType: 'hipaa',
						};
					}
					return {
						state: 'failed',
						reason: 'backend',
						details: 'Unhandled error response received',
						errorCode: response.status,
						backendFailureType: 'http_error',
					};
				}

				// Process all complete lines, except for the last one (which might be incomplete)
				while (lines.length > 1) {
					const line = lines.shift()!;

					const parsedData = mapStreamDefinitionResponse(JSON.parse(line), traceId);

					if (parsedData.type === 'other') {
						continue;
					} else if (
						parsedData.type === AIAnswerQueryStreamType.AnswerType ||
						parsedData.type === AIAnswerQueryStreamType.AnswerPart ||
						parsedData.type === AIAnswerQueryStreamType.GenerationType ||
						parsedData.type === AIAnswerQueryStreamType.Trace ||
						parsedData.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage
					) {
						if (generatorConfig.yieldAnswerPart) {
							yield parsedData;
						}
					} else if (parsedData.type === 'follow-up') {
						if (!finalResponse?.message || !variables?.followUpsEnabled) {
							continue;
						}

						finalResponse.message.nlpFollowUpResults = {
							followUps: parsedData.queries,
						};

						const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
							abTestId: 'kd_sain_show_no_answer_suggestions_exp',
						});

						if (!serveErrorStateSuggestedQueriesExp) {
							done = true;
						}
					} else if (parsedData.type === AIAnswerQueryStreamType.ErrorStateQuerySuggestions) {
						if (!finalResponse?.message) {
							continue;
						}

						finalResponse.message.nlpSuggestedQueries = parsedData.suggested_queries;

						done = true;
					} else {
						finalResponse = parsedData;

						onStreamFinalResponse?.();

						if (!variables?.followUpsEnabled) {
							done = true;
						}

						if (generatorConfig.yieldFinalResponse) {
							yield finalResponse;
						}
					}
				}
				// Keep the last (potentially incomplete) line in the buffer
				buffer = lines[0];
			}

			if (finalResponse?.type !== AIAnswerQueryStreamType.FinalResponse) {
				return {
					state: 'failed',
					reason: 'parsing',
					error: 'Unexpected final response value',
				};
			}

			return { state: 'complete', value: finalResponse };
		} catch (parsingError: unknown) {
			return { state: 'failed', reason: 'parsing', error: parsingError };
		}
	} catch (error: unknown) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			return { state: 'failed', reason: 'aborted' };
		}

		if (error instanceof DOMException && error.name === 'TimeoutError') {
			return { state: 'failed', reason: 'timeout' };
		}

		if (error instanceof Error) {
			return { state: 'failed', reason: 'error', details: error.message, error };
		}

		return { state: 'failed', reason: 'unknown' };
	}
}
